import { CbhIcon, Divider, Text, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { addMinutes, differenceInMinutes } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { useFormContext } from "react-hook-form";

import { DigitalClockInputBottomSheet } from "../../../DateTime/DigitalClockInputBottomSheet";
import { formatTimeWithTimezone } from "../../../utils/formatTimeWithTimezone";
import { type CreateShiftVerificationRequestFormValues } from "../types";
import { fixDayForShiftClockTimes } from "../utils";

interface CreateShiftVerificationRequestTimesFormProps {
  workplaceTimezone: string;
  shiftStartTime: Date;
}

export function CreateShiftVerificationRequestTimesForm(
  props: CreateShiftVerificationRequestTimesFormProps
) {
  const { workplaceTimezone, shiftStartTime } = props;
  const {
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext<CreateShiftVerificationRequestFormValues>();

  const clockInModalState = useModalState();
  const breakStartModalState = useModalState();
  const breakEndModalState = useModalState();
  const clockOutModalState = useModalState();

  const clockInTime = watch("shiftTimes.clockIn");
  const breakStartTime = watch("shiftTimes.breakStart");
  const breakEndTime = watch("shiftTimes.breakEnd");
  const clockOutTime = watch("shiftTimes.clockOut");

  const timeFields = [
    {
      fieldName: "shiftTimes.clockIn",
      modalState: clockInModalState,
      iconType: "clock",
      label: "Clock in",
      value: clockInTime,
    },
    {
      fieldName: "shiftTimes.breakStart",
      modalState: breakStartModalState,
      iconType: "break",
      label: "Break Start",
      value: breakStartTime,
    },
    {
      fieldName: "shiftTimes.breakEnd",
      modalState: breakEndModalState,
      iconType: "break",
      label: "Break End",
      value: breakEndTime,
    },
    {
      fieldName: "shiftTimes.clockOut",
      modalState: clockOutModalState,
      iconType: "clock",
      label: "Clock out",
      value: clockOutTime,
    },
  ] as const;

  const breakMinutes =
    isDefined(breakStartTime) && isDefined(breakEndTime)
      ? differenceInMinutes(breakEndTime, breakStartTime)
      : 0;
  const totalMinutes =
    isDefined(clockInTime) && isDefined(clockOutTime)
      ? differenceInMinutes(clockOutTime, clockInTime) - breakMinutes
      : undefined;

  const errorsText =
    errors.shiftTimes?.clockIn?.message ??
    errors.shiftTimes?.clockOut?.message ??
    errors.shiftTimes?.breakStart?.message ??
    errors.shiftTimes?.breakEnd?.message;

  const updateShiftTimeFields = async ({
    field,
    hour,
    minute,
  }: {
    field:
      | "shiftTimes.clockIn"
      | "shiftTimes.breakStart"
      | "shiftTimes.breakEnd"
      | "shiftTimes.clockOut";
    hour: number;
    minute: number;
  }) => {
    // Created the date based on shift start + hour and minute in local timezone
    const utcTargetTime = utcToZonedTime(shiftStartTime, workplaceTimezone);
    utcTargetTime.setHours(hour, minute, 0, 0);
    const updatedTime = zonedTimeToUtc(utcTargetTime, workplaceTimezone);

    const fixedTimes = fixDayForShiftClockTimes({
      shiftStart: shiftStartTime,
      clockTimes: {
        clockIn: field === "shiftTimes.clockIn" ? updatedTime : clockInTime,
        breakStart: field === "shiftTimes.breakStart" ? updatedTime : breakStartTime,
        breakEnd: field === "shiftTimes.breakEnd" ? updatedTime : breakEndTime,
        clockOut: field === "shiftTimes.clockOut" ? updatedTime : clockOutTime,
      },
    });

    if (fixedTimes.clockIn) {
      setValue("shiftTimes.clockIn", fixedTimes.clockIn, { shouldValidate: true });
    }

    if (fixedTimes.breakStart) {
      setValue("shiftTimes.breakStart", fixedTimes.breakStart, { shouldValidate: true });
    }

    if (fixedTimes.breakEnd) {
      setValue("shiftTimes.breakEnd", fixedTimes.breakEnd, { shouldValidate: true });
    }

    if (fixedTimes.clockOut) {
      setValue("shiftTimes.clockOut", fixedTimes.clockOut, { shouldValidate: true });
    }

    // Automatically set break-end when break-start is set, or vice versa
    if (
      field === "shiftTimes.breakStart" &&
      fixedTimes.breakStart &&
      (!fixedTimes.breakEnd || fixedTimes.breakEnd < fixedTimes.breakStart)
    ) {
      setValue("shiftTimes.breakEnd", addMinutes(fixedTimes.breakStart, 30), {
        shouldValidate: true,
      });
    } else if (
      field === "shiftTimes.breakEnd" &&
      fixedTimes.breakEnd &&
      (!breakStartTime || breakStartTime > fixedTimes.breakEnd)
    ) {
      setValue("shiftTimes.breakStart", addMinutes(fixedTimes.breakEnd, -30), {
        shouldValidate: true,
      });
    }

    // Trigger validation for all fields in the form
    await trigger();
  };

  return (
    <>
      {timeFields.map(({ fieldName, label, modalState, iconType, value }) => {
        return (
          <Stack key={fieldName} direction="row" spacing={3} alignItems="center">
            <CbhIcon size="medium" type={iconType} />
            <Text bold sx={{ color: (theme) => theme.palette.text.primary }}>
              {label}
            </Text>
            <Box flexGrow={1} textAlign="right">
              {fieldName === "shiftTimes.breakStart" && isDefined(breakStartTime) && (
                <IconButton
                  invert
                  variant="contained"
                  iconType="remove"
                  size="small"
                  title="Clear break"
                  sx={{ mx: 1 }}
                  onClick={() => {
                    setValue("shiftTimes.breakStart", undefined, { shouldValidate: true });
                    setValue("shiftTimes.breakEnd", undefined, { shouldValidate: true });
                  }}
                />
              )}
              <Button
                aria-label={`input-${fieldName}`}
                variant="outlined"
                endIconType="pencil"
                size="small"
                onClick={modalState.openModal}
              >
                {value ? formatTimeWithTimezone(value, workplaceTimezone, false) : "--:-- --"}
              </Button>
              <DigitalClockInputBottomSheet
                key={`input-${fieldName}`}
                modalState={modalState}
                initialTime={value}
                title={`Select ${label} time`}
                confirmButtonText="Save"
                minutesStep={1}
                onConfirm={async (updatedTime) => {
                  modalState.closeModal();
                  await updateShiftTimeFields({
                    field: fieldName,
                    hour: updatedTime.getHours(),
                    minute: updatedTime.getMinutes(),
                  });
                }}
              />
            </Box>
          </Stack>
        );
      })}
      {errorsText && (
        <Text variant="body2" color="error" align="center" sx={{ mt: 1 }}>
          {errorsText === "Required" ? "Please complete clock in/out." : errorsText}
        </Text>
      )}
      <Divider />
      <Stack direction="row" justifyContent="flex-end">
        <Text bold variant="h5" sx={{ color: (theme) => theme.palette.text.tertiary }} flexGrow={1}>
          Total
        </Text>
        <Text bold variant="h5">
          {totalMinutes ? `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m` : "--:--"}
        </Text>
      </Stack>
    </>
  );
}
